import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { adminAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, 
  Search,
  ChevronLeft,
  Plus,
  Trash2,
  Loader2,
  MapPin,
  Percent,
  Edit
} from 'lucide-react';
import { formatDateTime, formatCurrency } from '../lib/utils';

export default function AdminFieldOwners() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fieldOwners, setFieldOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [commissionOpen, setCommissionOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [newCommission, setNewCommission] = useState('');
  const [newOwner, setNewOwner] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    fetchFieldOwners();
  }, [user, navigate]);

  const fetchFieldOwners = async () => {
    try {
      const response = await api.get('/admin/users?role=field_owner');
      setFieldOwners(response.data || []);
    } catch (error) {
      console.error('Failed to fetch field owners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newOwner.name || !newOwner.email || !newOwner.password) {
      toast.error('Name, E-Mail und Passwort sind erforderlich');
      return;
    }

    setCreating(true);
    try {
      const params = new URLSearchParams({
        name: newOwner.name,
        email: newOwner.email,
        password: newOwner.password,
      });
      if (newOwner.phone) params.append('phone', newOwner.phone);

      await api.post(`/admin/users/field-owner?${params.toString()}`);
      toast.success('Feldbesitzer erstellt!');
      setCreateOpen(false);
      setNewOwner({ name: '', email: '', phone: '', password: '' });
      await fetchFieldOwners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (ownerId, ownerName) => {
    if (!window.confirm(`Feldbesitzer "${ownerName}" wirklich löschen? Alle zugehörigen Felder werden deaktiviert.`)) return;

    try {
      await api.delete(`/admin/users/${ownerId}`);
      toast.success('Feldbesitzer gelöscht');
      await fetchFieldOwners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleUpdateCommission = async () => {
    if (!selectedOwner || !newCommission) return;
    
    const rate = parseFloat(newCommission) / 100;
    if (rate < 0 || rate > 1) {
      toast.error('Provision muss zwischen 0% und 100% liegen');
      return;
    }
    
    try {
      await adminAPI.updateCommission(selectedOwner.id, rate);
      toast.success(`Provision für ${selectedOwner.name} auf ${newCommission}% aktualisiert`);
      setCommissionOpen(false);
      setSelectedOwner(null);
      setNewCommission('');
      await fetchFieldOwners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  const openCommissionDialog = (owner) => {
    setSelectedOwner(owner);
    setNewCommission(((owner.commission_rate || 0.1) * 100).toFixed(0));
    setCommissionOpen(true);
  };

  const filteredOwners = fieldOwners.filter(o =>
    o.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-64 mb-8" />
          <Skeleton className="h-96 rounded-2xl" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-field-owners">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-4 w-full sm:w-auto">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="font-display font-bold text-xl sm:text-2xl text-tunis-blue">
                Feldbesitzer
              </h1>
              <p className="text-sm text-muted-foreground">{fieldOwners.length} registriert</p>
            </div>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary w-full sm:w-auto">
                <Plus className="w-5 h-5 mr-2" />
                Neu erstellen
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Neuen Feldbesitzer erstellen</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={newOwner.name}
                    onChange={(e) => setNewOwner(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Geschäftsname"
                  />
                </div>
                <div className="space-y-2">
                  <Label>E-Mail *</Label>
                  <Input
                    type="email"
                    value={newOwner.email}
                    onChange={(e) => setNewOwner(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="email@beispiel.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefon</Label>
                  <Input
                    type="tel"
                    value={newOwner.phone}
                    onChange={(e) => setNewOwner(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+216 XX XXX XXX"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Passwort *</Label>
                  <Input
                    type="password"
                    value={newOwner.password}
                    onChange={(e) => setNewOwner(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Mindestens 6 Zeichen"
                  />
                </div>
                <Button type="submit" className="w-full btn-primary" disabled={creating}>
                  {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Erstellen'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Feldbesitzer suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12"
          />
        </div>

        {/* Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden sm:table-cell">E-Mail</TableHead>
                  <TableHead className="hidden md:table-cell">Telefon</TableHead>
                  <TableHead className="text-center">Provision</TableHead>
                  <TableHead className="hidden md:table-cell text-center">Wallet</TableHead>
                  <TableHead className="hidden lg:table-cell">Registriert</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOwners.map((owner) => (
                  <TableRow key={owner.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-tunis-blue flex-shrink-0" />
                        <div className="min-w-0">
                          <span className="block truncate">{owner.name}</span>
                          <span className="block sm:hidden text-xs text-muted-foreground truncate">{owner.email || '-'}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-muted-foreground">{owner.email || '-'}</TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{owner.phone || '-'}</TableCell>
                    <TableCell className="text-center">
                      <Badge 
                        className="bg-orange-500/10 text-orange-600 cursor-pointer hover:bg-orange-500/20"
                        onClick={() => openCommissionDialog(owner)}
                      >
                        <Percent className="w-3 h-3 mr-1" />
                        {((owner.commission_rate || 0.1) * 100).toFixed(0)}%
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-center">
                      <Badge className="bg-green-500/10 text-green-600">
                        {formatCurrency(owner.wallet_balance || 0)}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                      {formatDateTime(owner.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-tunis-blue hover:bg-tunis-blue/10"
                          onClick={() => openCommissionDialog(owner)}
                          title="Provision bearbeiten"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(owner.id, owner.name)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {filteredOwners.length === 0 && (
              <div className="text-center py-12">
                <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-muted-foreground">Keine Feldbesitzer gefunden</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Commission Dialog */}
        <Dialog open={commissionOpen} onOpenChange={setCommissionOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Provision bearbeiten</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium">{selectedOwner?.name}</p>
                <p className="text-sm text-muted-foreground">{selectedOwner?.email}</p>
              </div>
              <div className="space-y-2">
                <Label>Provision (%)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={newCommission}
                    onChange={(e) => setNewCommission(e.target.value)}
                    placeholder="z.B. 10"
                  />
                  <span className="text-muted-foreground">%</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Der Feldbesitzer erhält {100 - (parseFloat(newCommission) || 0)}% jeder Buchung.
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setCommissionOpen(false)}>
                  Abbrechen
                </Button>
                <Button className="flex-1 btn-primary" onClick={handleUpdateCommission}>
                  Speichern
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
